import React, { useCallback, useEffect, useState, createContext, useContext } from 'react';
import { dictionary, Lang } from './dictionary';
interface LanguageContextValue {
  lang: Lang;
  setLang: (l: Lang) => void;
  toggle: () => void;
  t: <T = string>(getter: (d: typeof dictionary, lang: Lang) => any) => T;
}
const LanguageContext = createContext<LanguageContextValue | null>(null);
const STORAGE_KEY = 'aeb_lang';
function detectInitial(): Lang {
  if (typeof window === 'undefined') return 'en';
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'en' || saved === 'es') return saved;
    const browser = (navigator.language || 'en').toLowerCase();
    return browser.startsWith('es') ? 'es' : 'en';
  } catch {
    return 'en';
  }
}
export function LanguageProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [lang, setLangState] = useState<Lang>('en');
  useEffect(() => {
    setLangState(detectInitial());
  }, []);
  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = lang;
    }
  }, [lang]);
  const setLang = useCallback((l: Lang) => {
    setLangState(l);
    try {
      localStorage.setItem(STORAGE_KEY, l);
    } catch {}
  }, []);
  const toggle = useCallback(() => {
    setLangState(prev => {
      const next: Lang = prev === 'en' ? 'es' : 'en';
      try {
        localStorage.setItem(STORAGE_KEY, next);
      } catch {}
      return next;
    });
  }, []);
  // Generic accessor: pass a function that picks from the dictionary using lang
  const t = useCallback(<T,>(getter: (d: typeof dictionary, lang: Lang) => any): T => {
    return getter(dictionary, lang) as T;
  }, [lang]);
  return <LanguageContext.Provider value={{
    lang,
    setLang,
    toggle,
    t
  }} data-id="element-678">
      {children}
    </LanguageContext.Provider>;
}
export function useLang() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLang must be used within LanguageProvider');
  return ctx;
}
// Convenience hook — pulls a node from the dictionary and returns the value
// for the active language. Use like: const heroT = useT(d => d.hero); heroT.headlineStart.en/es resolved.
// Simpler usage: useTr(d => d.hero.headlineStart) -> string for current lang.
export function useTr(getter: (d: typeof dictionary) => {
  en: any;
  es: any;
}): any {
  const {
    lang
  } = useLang();
  const node = getter(dictionary);
  return node[lang];
}